import React, { useState, useEffect } from 'react';

interface VideoHeroProps {
  video_url?: string;
  poster?: string;
  title?: string;
  subtitle?: string;
  cta_text?: string;
  cta_link?: string;
  phone?: string;
}

export const VideoHero: React.FC<VideoHeroProps> = ({
  video_url = '',
  poster = '',
  title = '',
  subtitle = '',
  cta_text = '',
  cta_link = ''
}) => {
  const [isAnimated, setIsAnimated] = useState(false);

  // Get WordPress data
  const wpData = (window as any).naiThemeData || {};

  // Define constants that work with or without config - NO CONDITIONALS
  const videoSrc = video_url || '/public/content/videos/hero-video.mp4';
  const posterSrc = poster || '/public/content/images/hero.webp';
  const heroTitle = title || (wpData.siteName ? `Professional ${wpData.siteName} Services` : 'Professional Roofing and Remodeling Services');
  const heroSubtitle = subtitle || 'Trusted San Diego roofers specializing in residential and commercial roofing. Get your free roof inspection from licensed contractors with 25+ years experience and comprehensive warranties.';
  const heroCtaText = cta_text || 'Request Free Consultation';
  const heroCtaLink = cta_link || '#';

  // Animation effect - always runs consistently
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimated(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Check if mobile for conditional loading
  const isMobile = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // ALWAYS return the same JSX structure - no conditional returns
  return (
    <div className="hero-video-container relative h-screen w-full overflow-hidden">
      {/* Poster background */}
      <div
        className="absolute z-0 w-full h-full"
        style={{
          backgroundImage: `url('${posterSrc}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>

      {/* Video */}
      <video
        id="hero-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute z-0 w-full h-full object-cover"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          willChange: 'transform'
        }}
        poster={posterSrc}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <div className="absolute z-10 w-full h-full bg-black/50"></div>

      {/* Content */}
      <div className="relative z-20 flex h-full items-center justify-start">
        <div className="container px-4 sm:px-6 md:px-12 flex items-center h-full">
          {/* Glassmorphic Background Container */}
          <div
            className="max-w-2xl lg:max-w-3xl text-white p-6 sm:p-8 lg:p-10 rounded-2xl"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            {/* Title */}
            <h1 className={`text-3xl sm:text-4xl font-bold leading-tight mb-6 sm:mb-7 ${
              isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            } transition-all ease-out duration-700`}>
              {heroTitle}
            </h1>

            {/* Subtitle */}
            <p className={`text-base sm:text-lg font-light mb-6 sm:mb-8 ${
              isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            } transition-all ease-out duration-700`}>
              {heroSubtitle}
            </p>

            {/* CTA button */}
            <div className={`hidden sm:block ${
              isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            } transition-all ease-out duration-700`}>
              <a href={heroCtaLink} className="btn-secondary-cta">
                {heroCtaText}
              </a>
            </div>

            {/* Social Proof */}
            <div className={`mt-8 sm:mt-12 flex items-center space-x-3 sm:space-x-4 ${
              isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
            } transition-all ease-out duration-700`}>
              <img
                className="h-12 sm:h-16"
                src="/public/content/images/san-diego-roofing-contractors-satisfied-customers.webp"
                alt="San Diego Roofing Contractors Satisfied Customers"
                width="150"
                height="64"
                {...(isMobile() ? { loading: "eager" } : { fetchpriority: "high" })}
              />
              <div>
                <p className="font-bold text-2xl sm:text-3xl">2,000+</p>
                <p className="text-sm sm:text-base font-light">San Diego Homeowners Served</p>
              </div>
              <div className="hidden sm:block border-l border-white/30 pl-4 sm:pl-6">
                <p className="font-bold text-2xl sm:text-3xl">25+</p>
                <p className="text-sm sm:text-base font-light">Years Experience</p>
              </div>
              <div className="hidden md:block border-l border-white/30 pl-4 sm:pl-6">
                <p
                  className="font-bold text-2xl sm:text-3xl"
                  style={{
                    color: '#FFB800',
                    textShadow: '0 0 10px rgba(255, 184, 0, 0.5)'
                  }}
                >
                  ★★★★★
                </p>
                <p className="text-sm sm:text-base font-light">Licensed & Insured</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};